import { Check, ChevronDown, Search, X } from "lucide-react";
import React, { useId, useState } from "react";
import { cn } from "utils/cn";
import Button from "./Button";
import Input from "./Input";

export type SelectValue = string | number;

export interface SelectOption {
	label: string;
	value: SelectValue;
	description?: string;
	disabled?: boolean;
}

export interface SelectChangeEvent {
	target: {
		value: SelectValue | SelectValue[];
		name?: string;
	};
}

export interface SelectProps
	extends Omit<
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		"type" | "onChange" | "value" | "defaultValue"
	> {
	options?: SelectOption[];
	value?: SelectValue | SelectValue[];
	defaultValue?: SelectValue | SelectValue[];
	placeholder?: string;
	multiple?: boolean;
	required?: boolean;
	label?: React.ReactNode;
	description?: React.ReactNode;
	error?: React.ReactNode;
	searchable?: boolean;
	clearable?: boolean;
	loading?: boolean;
	id?: string;
	name?: string;
	onChange?: (event: SelectChangeEvent) => void;
	onValueChange?: (value: SelectValue | SelectValue[]) => void;
	onOpenChange?: (open: boolean) => void;
}

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
	(
		{
			className,
			options = [],
			value,
			defaultValue,
			placeholder = "Select an option",
			multiple = false,
			disabled = false,
			required = false,
			label,
			description,
			error,
			searchable = false,
			clearable = false,
			loading = false,
			id,
			name,
			onChange,
			onValueChange,
			onOpenChange,
			...props
		},
		ref,
	) => {
		// Extract custom props that shouldn't be passed to the button element
		// These are React event handlers that don't belong on native HTML elements
		const buttonProps = { ...props };
		// Remove any custom handlers that might have been passed
		delete (buttonProps as any).onValueChange;
		delete (buttonProps as any).onOpenChange;
		const [isOpen, setIsOpen] = useState(false);
		const [searchTerm, setSearchTerm] = useState("");

		const generatedId = useId();
		const selectId = id ?? generatedId;

		const currentValue = value ?? defaultValue;

		const filteredOptions =
			searchable && searchTerm
				? options.filter(
						(option) =>
							option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
							option.value
								.toString()
								.toLowerCase()
								.includes(searchTerm.toLowerCase()),
				  )
				: options;

		const getSelectedDisplay = () => {
			if (multiple) {
				const selectedValues = Array.isArray(currentValue) ? currentValue : [];
				if (selectedValues.length === 0) return placeholder;

				const selectedOptions = options.filter((option) =>
					selectedValues.includes(option.value),
				);
				if (selectedOptions.length === 0) return placeholder;
				if (selectedOptions.length === 1) return selectedOptions[0].label;
				return `${selectedOptions.length} items selected`;
			}

			if (
				currentValue === undefined ||
				currentValue === null ||
				currentValue === ""
			) {
				return placeholder;
			}

			const selectedOption = options.find(
				(option) => option.value === currentValue,
			);
			return selectedOption ? selectedOption.label : placeholder;
		};

		const handleToggle = () => {
			if (disabled) return;
			const newIsOpen = !isOpen;
			setIsOpen(newIsOpen);
			onOpenChange?.(newIsOpen);

			if (!newIsOpen) {
				setSearchTerm("");
			}
		};

		const emitChange = (nextValue: SelectValue | SelectValue[]) => {
			onValueChange?.(nextValue);

			if (onChange) {
				onChange({
					target: {
						value: nextValue,
						name,
					},
				});
			}
		};

		const handleOptionSelect = (option: SelectOption) => {
			if (multiple) {
				const currentValues = Array.isArray(value)
					? value
					: Array.isArray(defaultValue)
					? defaultValue
					: [];
				const updatedValue = currentValues.includes(option.value)
					? currentValues.filter(
							(existingValue) => existingValue !== option.value,
					  )
					: [...currentValues, option.value];
				emitChange(updatedValue);
			} else {
				emitChange(option.value);
				setIsOpen(false);
				onOpenChange?.(false);
			}
		};

		const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			emitChange(multiple ? [] : "");
		};

		const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
			setSearchTerm(event.target.value);
		};

		const isSelected = (optionValue: SelectValue) => {
			if (multiple) {
				const selectedValues = Array.isArray(currentValue) ? currentValue : [];
				return selectedValues.includes(optionValue);
			}
			return currentValue === optionValue;
		};

		const hasValue = multiple
			? Array.isArray(currentValue) && currentValue.length > 0
			: currentValue !== undefined &&
			  currentValue !== null &&
			  currentValue !== "";

		const hiddenSelectValue = (() => {
			if (multiple) {
				return Array.isArray(currentValue)
					? currentValue.map((item) => String(item))
					: [];
			}
			if (
				currentValue === undefined ||
				currentValue === null ||
				currentValue === ""
			) {
				return "";
			}
			return String(currentValue);
		})();

		return (
			<div className={cn("relative", className)}>
				{label && (
					<label
						htmlFor={selectId}
						className={cn(
							"text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block",
							error ? "text-destructive" : "text-foreground",
						)}
					>
						{label}
						{required && <span className="text-destructive ml-1">*</span>}
					</label>
				)}

				<div className="relative">
					<button
						ref={ref}
						id={selectId}
						type="button"
						className={cn(
							"flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
							error && "border-destructive focus:ring-destructive",
							!hasValue && "text-muted-foreground",
						)}
						onClick={handleToggle}
						disabled={disabled}
						aria-expanded={isOpen}
						aria-haspopup="listbox"
						{...buttonProps}
					>
						<span className="truncate flex-1 text-left min-w-0">
							{getSelectedDisplay()}
						</span>

						<div className="flex items-center gap-1 flex-shrink-0">
							{loading && (
								<svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
										fill="none"
									/>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
							)}

							{clearable && hasValue && !loading && (
								<Button
									variant="ghost"
									size="icon"
									className="h-4 w-4"
									onClick={handleClear}
								>
									<X className="h-3 w-3" />
								</Button>
							)}

							<ChevronDown
								className={cn(
									"h-4 w-4 transition-transform flex-shrink-0",
									isOpen && "rotate-180",
								)}
							/>
						</div>
					</button>

					<select
						name={name}
						value={hiddenSelectValue}
						onChange={() => undefined}
						className="sr-only"
						tabIndex={-1}
						multiple={multiple}
						required={required}
					>
						{!multiple && <option value="">Select...</option>}
						{options.map((option) => (
							<option key={option.value} value={String(option.value)}>
								{option.label}
							</option>
						))}
					</select>

					{isOpen && (
						<div className="absolute z-[9998] w-full mt-1 bg-white text-black border border-border rounded-md shadow-lg">
							{searchable && (
								<div className="p-2 border-b">
									<div className="relative">
										<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
										<Input
											placeholder="Search options..."
											value={searchTerm}
											onChange={handleSearchChange}
											className="pl-8"
										/>
									</div>
								</div>
							)}

							<div className="py-1 max-h-60 overflow-auto">
								{filteredOptions.length === 0 ? (
									<div className="px-3 py-2 text-sm text-muted-foreground">
										{searchTerm ? "No options found" : "No options available"}
									</div>
								) : (
									filteredOptions.map((option) => (
										<div
											key={option.value}
											className={cn(
												"relative flex cursor-pointer select-none items-center rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
												isSelected(option.value) &&
													"bg-primary text-primary-foreground",
												option.disabled && "pointer-events-none opacity-50",
											)}
											onClick={() =>
												!option.disabled && handleOptionSelect(option)
											}
										>
											<span className="flex-1">{option.label}</span>
											{multiple && isSelected(option.value) && (
												<Check className="h-4 w-4" />
											)}
											{option.description && (
												<span className="text-xs text-muted-foreground ml-2">
													{option.description}
												</span>
											)}
										</div>
									))
								)}
							</div>
						</div>
					)}
				</div>

				{description && !error && (
					<p className="text-sm text-muted-foreground mt-1">{description}</p>
				)}

				{error && <p className="text-sm text-destructive mt-1">{error}</p>}
			</div>
		);
	},
);

Select.displayName = "Select";

export default Select;
